import type { Site } from "../types";

export const SITES: Site[] = [
  {
    id: "big-bend-np",
    name: "Big Bend National Park",
    shortName: "Big Bend NP",
    region: "Texas",
    state: "TX",
    latitude: 29.1833,
    longitude: -102.9583,
    bortle: 1,
    bortleNote: "Typically Bortle 1–2. One of the darkest units in the lower 48.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: false,
    permit: true,
    tags: ["drive-up", "overnight", "designated-camp", "short-rigs", "permit"],
    whyGood:
      "International Dark Sky Park on the Rio Grande. Horizon-to-horizon Milky Way on a new moon, with enough infrastructure to stay several nights.",
    accessNotes:
      "Paved park roads to the main campgrounds. Rio Grande Village is the most practical overnight for vans; Chisos Basin is tighter and has length limits. Primitive roadside camps need a permit and are often a better sky than the developed loops. Check NPS for closures, generators, and night-sky program hours.",
    lengthNotes:
      "Chisos Basin is a poor fit for longer rigs (often ~24 ft). Rio Grande Village and Cottonwood are more van-friendly. Oversize Class A coaches are a struggle in this park.",
    etiquette:
      "Stay in designated sites. Lights out after quiet hours — use red light only. Pack out everything; water is scarce. Do not set up in pullouts that are not legal camps.",
    officialUrl: "https://www.nps.gov/bibe/index.htm",
  },
  {
    id: "big-bend-ranch",
    name: "Big Bend Ranch State Park",
    shortName: "Big Bend Ranch",
    region: "Texas",
    state: "TX",
    latitude: 29.476,
    longitude: -103.965,
    bortle: 1,
    bortleNote: "Bortle 1. As dark as the national park, with more primitive camping.",
    overnight: "mixed",
    driveUp: true,
    lengthFriendly: true,
    highClearance: true,
    permit: true,
    tags: ["drive-up", "overnight", "dispersed", "length-friendly", "high-clearance", "permit"],
    whyGood:
      "Huge, empty west-Texas ranch country. Self-contained vehicles can sit under a true desert sky without the national-park campground crowds.",
    accessNotes:
      "FM 170 (River Road) is scenic and paved. Interior ranch roads are dirt; high-clearance helps and rain shuts them down. Primitive camps are first-come with a Texas State Parks permit. Sauceda is the interior hub. Not a hookup park — this is boondocking with a permit.",
    lengthNotes:
      "River Road pull-in camps and some interior sites take a 20–24 ft van easily. Long trailers and low-clearance coaches should stay on or near FM 170.",
    etiquette:
      "Permit required. Closed gates stay closed. No off-road driving. Fires only where posted. This is working ranch and wilderness — leave no trace, keep night lighting minimal.",
    officialUrl: "https://tpwd.texas.gov/state-parks/big-bend-ranch",
  },
  {
    id: "davis-mountains",
    name: "Davis Mountains State Park",
    shortName: "Davis Mountains",
    region: "Texas",
    state: "TX",
    latitude: 30.5994,
    longitude: -103.9294,
    bortle: 2,
    bortleNote: "Bortle 2. McDonald Observatory is next door.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "Easy paved access, a real dark-sky neighborhood, and public programs at McDonald Observatory. A comfortable multi-night base without four-wheel-drive drama.",
    accessNotes:
      "Straight shot on TX-118 through Fort Davis. Standard state-park campground with some hookups at Indian Lodge / the developed loops. Observatory dark-sky parties are ticketed — worth planning around. Fort Davis has groceries and fuel.",
    lengthNotes:
      "Developed loops handle typical vans and many 25–30 ft trailers. Check site length when you reserve. The town of Fort Davis is close if you need a backup hotel night.",
    etiquette:
      "Reserve when you can; this park fills on observatory event weekends. Dim lights toward the sky. Do not set up on the observatory grounds overnight.",
    officialUrl: "https://tpwd.texas.gov/state-parks/davis-mountains",
  },
  {
    id: "guadalupe-mountains",
    name: "Guadalupe Mountains National Park",
    shortName: "Guadalupe Mountains",
    region: "Texas",
    state: "TX",
    latitude: 31.8954,
    longitude: -104.8277,
    bortle: 2,
    bortleNote: "Bortle 2. High desert with a dark west-Texas sky.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: false,
    tags: ["drive-up", "overnight", "designated-camp", "short-rigs"],
    whyGood:
      "A dark Chihuahuan Desert sky at the base of Texas’s highest peak. Quieter than Big Bend, with a simple drive-in campground.",
    accessNotes:
      "US-62/180 to Pine Springs. The campground is first-come, tents and small vehicles. No hookups, limited shade, wind is common. Dog Canyon on the north side is even quieter and a longer drive.",
    lengthNotes:
      "Pine Springs is built for small rigs and vans, not big coaches. If you need length, look at nearby BLM or the town of White’s City / Carlsbad as a last resort (those are much brighter).",
    etiquette:
      "Stay in numbered sites. Water is limited. Night hiking on trails is fine; roadside sleeping outside the campground is not.",
    officialUrl: "https://www.nps.gov/gumo/index.htm",
  },
  {
    id: "kickapoo-cavern",
    name: "Kickapoo Cavern State Park",
    shortName: "Kickapoo Cavern",
    region: "Texas",
    state: "TX",
    latitude: 29.6097,
    longitude: -100.4436,
    bortle: 2,
    bortleNote: "Bortle 2–3. Darker than the Hill Country, closer than Big Bend.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "A West Texas state park that is actually dark, without the all-day drive to the Big Bend. Good stepping-stone for a first overnight under real stars.",
    accessNotes:
      "West of Uvalde off US-90 / RM 674. Confirm current hours and camping status before you go — this park has had limited operations in the past. Developed campsites, no urban backup nearby.",
    lengthNotes:
      "Standard state-park pads. Typical vans fit; check the reservation map for trailer length.",
    etiquette:
      "Cave and bat areas have their own rules. Quiet hours matter here — it is a small park. Pack out trash.",
    officialUrl: "https://tpwd.texas.gov/state-parks/kickapoo-cavern",
  },
  {
    id: "south-llano",
    name: "South Llano River State Park",
    shortName: "South Llano",
    region: "Texas",
    state: "TX",
    latitude: 30.446,
    longitude: -99.805,
    bortle: 4,
    bortleNote: "Bortle 3–4. Not pristine, but a useful dark-ish overnight west of the Hill Country glow.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "An accessible overnight if Big Bend is too far this weekend. Darker than Austin or San Antonio, with river camps and a straightforward drive from central Texas.",
    accessNotes:
      "US-377 at Junction. Reservable state-park campsites, some with hookups. The river corridor is the stay; walk a little away from the loops for a better sky.",
    lengthNotes:
      "Pull-through and back-in pads handle vans and many mid-size trailers. Easy access, no dirt-road drama.",
    etiquette:
      "This is a busy family park on weekends. Keep lighting low, generators to posted hours, and do not treat it like a wilderness sanctuary — it isn’t one.",
    officialUrl: "https://tpwd.texas.gov/state-parks/south-llano-river",
  },
  {
    id: "cosmic-campground",
    name: "Cosmic Campground",
    shortName: "Cosmic Campground",
    region: "Southwest",
    state: "NM",
    latitude: 33.4795,
    longitude: -108.9217,
    bortle: 1,
    bortleNote: "Bortle 1. International Dark Sky Sanctuary — among the darkest public sites in the U.S.",
    overnight: "dispersed",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "dispersed", "length-friendly"],
    whyGood:
      "A purpose-built dark-sky sanctuary on Gila National Forest land. Concrete pads, a horizon that goes forever, and no town lights on the skyline.",
    accessNotes:
      "US-180 north of Glenwood, then a short forest road. Usually 2WD when dry. Primitive: vault toilet, no water, no hookups, no fee. First-come. Bring everything. Alma / Reserve are the nearest supplies.",
    lengthNotes:
      "Pads and the loop are van- and small-RV friendly. Large fifth-wheels can manage when the road is dry; don’t try it in mud.",
    etiquette:
      "This place exists for astronomy. White lights are the enemy. Keep conversations and generators down. Do not occupy the observing pads as a generic RV dump.",
    officialUrl: "https://www.fs.usda.gov/r03/gila",
  },
  {
    id: "chaco-culture",
    name: "Chaco Culture National Historical Park",
    shortName: "Chaco",
    region: "Southwest",
    state: "NM",
    latitude: 36.0642,
    longitude: -107.9568,
    bortle: 2,
    bortleNote: "Bortle 2. Famous dark skies over the great houses.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: false,
    tags: ["drive-up", "overnight", "designated-camp", "short-rigs"],
    whyGood:
      "A dark basin far from Albuquerque’s sprawl, with an on-site campground and night-sky programs. The archaeology makes a multi-night stay easy to justify.",
    accessNotes:
      "Washboards on the county roads in — passenger cars do it slowly. Gallo Campground is the only overnight in the park. No hookups. Fuel up in Bloomfield, Cuba, or Crownpoint. Roads get ugly when wet.",
    lengthNotes:
      "Gallo is aimed at tents and small RVs (often around 30 ft max). Long rigs should not commit to the washboard approach.",
    etiquette:
      "Stay in Gallo. Sites among ruins are day-use. Night photography of great houses has extra rules — ask at the visitor center. Pack out water jugs.",
    officialUrl: "https://www.nps.gov/chcu/index.htm",
  },
  {
    id: "city-of-rocks-nm",
    name: "City of Rocks State Park",
    shortName: "City of Rocks",
    region: "Southwest",
    state: "NM",
    latitude: 32.5876,
    longitude: -107.9753,
    bortle: 2,
    bortleNote: "Bortle 2–3. Dark for a developed state park, easy I-10 access.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "Boulder campsites, a dark southern New Mexico sky, and a short hop off I-10. A practical overnight that still feels like somewhere.",
    accessNotes:
      "NM-61 between Deming and Silver City. Paved to the park. Mix of electric and primitive sites tucked into the rocks. Wind and sun are the weather story.",
    lengthNotes:
      "Many sites take vans; a few squeeze longer trailers. The rock maze is not a big-rig park — pick your pad from the map.",
    etiquette:
      "Sites are close. Keep lighting and generators considerate. Do not climb on fragile desert varnish just for a tripod shot.",
    officialUrl: "https://www.emnrd.nm.gov/spd/find-a-park/city-of-rocks-state-park/",
  },
  {
    id: "datil-well",
    name: "Datil Well Campground",
    shortName: "Datil Well",
    region: "Southwest",
    state: "NM",
    latitude: 34.145,
    longitude: -107.841,
    bortle: 1,
    bortleNote: "Bortle 1–2. Plains of San Agustin, near the Very Large Array.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "BLM campground with pull-throughs, a historic well, and a wide-open dark sky. Larger vans actually fit. The VLA is a short daylight side trip.",
    accessNotes:
      "US-60 at Datil. Paved to the entrance. Vault toilets, no hookups, modest fee. Pie Town and Magdalena are the service towns. Winter is cold and empty; summer monsoon can soak the loops.",
    lengthNotes:
      "One of the better length-friendly dark sites on this list. Pull-throughs work for vans and many trailers. Still dry camping.",
    etiquette:
      "Quiet high-desert neighborhood. Do not drone the VLA. Pack out trash; the well is historic, not a dump station.",
    officialUrl: "https://www.blm.gov/visit/datil-well-campground",
  },
  {
    id: "natural-bridges",
    name: "Natural Bridges National Monument",
    shortName: "Natural Bridges",
    region: "Southwest",
    state: "UT",
    latitude: 37.6093,
    longitude: -109.977,
    bortle: 2,
    bortleNote: "Bortle 2. The first International Dark Sky Park.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: false,
    tags: ["drive-up", "overnight", "designated-camp", "short-rigs"],
    whyGood:
      "The park that started the International Dark Sky Park program. A small, dark campground on Cedar Mesa with a night sky that still earns the title.",
    accessNotes:
      "UT-275 off UT-95. Paved. Tiny campground, first-come or as posted. No hookups. Blanding and Mexican Hat are the supply towns. Combine with Bears Ears / Valley of the Gods.",
    lengthNotes:
      "Sites are generally in the mid-20 ft range. This is a short-rig park. Overflow camping is not a thing inside the monument.",
    etiquette:
      "Lights out. The whole point of this park at night is darkness. Stay on slickrock and trails; cryptobiotic soil does not grow back on your timeline.",
    officialUrl: "https://www.nps.gov/nabr/index.htm",
  },
  {
    id: "canyonlands-needles",
    name: "Canyonlands — Needles District",
    shortName: "Needles",
    region: "Southwest",
    state: "UT",
    latitude: 38.168,
    longitude: -109.761,
    bortle: 2,
    bortleNote: "Bortle 2. Darker and quieter than Arches / Moab.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: false,
    tags: ["drive-up", "overnight", "designated-camp", "short-rigs"],
    whyGood:
      "Needles sits far enough from Moab that the sky goes properly dark. Red rock, quiet loops, and a multi-night feel the Island-in-the-Sky overlooks don’t have.",
    accessNotes:
      "UT-211 is paved to the visitor center and Squaw Flat / Needles campground. Interior four-wheel-drive roads are a different trip. Reserve campground sites when the system is open. Needles Outpost (private, seasonal) is the nearby backup.",
    lengthNotes:
      "Campground pads are compact. Vans are the sweet spot; long trailers fight the loops. Do not take a big rig down Elephant Hill.",
    etiquette:
      "Cryptobiotic soil, again. Night photography is welcome from camp and viewpoints — not from off-trail slickrock. Quiet hours are enforced.",
    officialUrl: "https://www.nps.gov/cany/planyourvisit/needles.htm",
  },
  {
    id: "goblin-valley",
    name: "Goblin Valley State Park",
    shortName: "Goblin Valley",
    region: "Southwest",
    state: "UT",
    latitude: 38.5739,
    longitude: -110.7056,
    bortle: 2,
    bortleNote: "Bortle 2. Dark San Rafael Desert; BLM nearby is even darker.",
    overnight: "mixed",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "dispersed", "length-friendly"],
    whyGood:
      "A dark, weird, drive-up desert with a developed campground and excellent BLM dispersed camping in the Temple Mountain area for larger vehicles.",
    accessNotes:
      "Paved to the state park. The developed campground is reservable. For length and quiet, many self-contained vans use nearby BLM around Temple Mountain / Goblin Valley Road — confirm current BLM rules. Hanksville is fuel and food.",
    lengthNotes:
      "Park campground is mixed. BLM dispersed off the main road is the length-friendly move for 20–30 ft vans. Soft sand after rain.",
    etiquette:
      "Stay on existing pads and roads. The goblins are not climbing gyms. Dispersed camping still means pack it all out and keep the night sky dark.",
    officialUrl: "https://stateparks.utah.gov/parks/goblin-valley/",
  },
  {
    id: "capitol-reef",
    name: "Capitol Reef — Fruita",
    shortName: "Capitol Reef",
    region: "Southwest",
    state: "UT",
    latitude: 38.2833,
    longitude: -111.2472,
    bortle: 2,
    bortleNote: "Bortle 2–3 at Fruita; darker in Cathedral Valley.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "A comfortable orchard campground in a dark-ish park, with Cathedral Valley as the rougher, darker sequel if your vehicle can handle it.",
    accessNotes:
      "UT-24 is paved through the park. Fruita Campground is the easy overnight. Cathedral Valley is a high-clearance loop — not for a long van in mud or snow. Torrey has services.",
    lengthNotes:
      "Fruita can take many vans and some trailers; reserve. Cathedral Valley is for shorter, higher-clearance rigs only.",
    etiquette:
      "Fruit is not free for the picking off historic trees unless posted. Night lighting in Fruita carries — dim it. Cathedral Valley is remote; tell someone your plan.",
    officialUrl: "https://www.nps.gov/care/index.htm",
  },
  {
    id: "grand-staircase",
    name: "Grand Staircase–Escalante",
    shortName: "Grand Staircase",
    region: "Southwest",
    state: "UT",
    latitude: 37.6506,
    longitude: -111.4147,
    bortle: 1,
    bortleNote: "Bortle 1–2 across much of the monument.",
    overnight: "dispersed",
    driveUp: true,
    lengthFriendly: true,
    highClearance: true,
    tags: ["drive-up", "overnight", "dispersed", "length-friendly", "high-clearance"],
    whyGood:
      "One of the great American dark-sky empties. Dispersed BLM camping and a handful of developed sites, with enough room for a self-contained van to sit still for days.",
    accessNotes:
      "Hole-in-the-Rock Road to Devil’s Garden is the classic starter (usually 2WD when dry, washboard). Conditions change fast. Escalante and Boulder are the towns. Check BLM / GSENM for fire restrictions and road reports.",
    lengthNotes:
      "Devil’s Garden and many roadside dispersals take vans. Deep sand and ledges further down Hole-in-the-Rock are not for long or low vehicles.",
    etiquette:
      "Dispersed does not mean random. Use existing sites, stay off cryptobiotic soil, and never block a two-track. This landscape reads every tire scar.",
    officialUrl: "https://www.blm.gov/programs/national-conservation-lands/utah/grand-staircase-escalante-national-monument",
  },
  {
    id: "goosenecks",
    name: "Goosenecks State Park",
    shortName: "Goosenecks",
    region: "Southwest",
    state: "UT",
    latitude: 37.1745,
    longitude: -109.927,
    bortle: 2,
    bortleNote: "Bortle 2. A dark overlook above the San Juan River.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "Primitive, cheap, paved-access camping on a cliff above one of the great river meanders. Dark, simple, and van-easy.",
    accessNotes:
      "UT-316 off US-163 near Mexican Hat. Short paved spur. Vault toilets, no water, no hookups. Wind can be violent. Mexican Hat / Bluff for fuel.",
    lengthNotes:
      "Open overlook camping — vans and many trailers fit. There are no defined long pull-throughs; pick a level pad and chock well.",
    etiquette:
      "No guardrail attitude: keep people and tripods back from the edge at night. Pack out all water and trash. The overlook is the campground — don’t sprawl.",
    officialUrl: "https://stateparks.utah.gov/parks/goosenecks/",
  },
  {
    id: "valley-of-the-gods",
    name: "Valley of the Gods",
    shortName: "Valley of the Gods",
    region: "Southwest",
    state: "UT",
    latitude: 37.274,
    longitude: -109.922,
    bortle: 1,
    bortleNote: "Bortle 1–2. Monument Valley’s quieter, public-land cousin.",
    overnight: "dispersed",
    driveUp: true,
    lengthFriendly: false,
    highClearance: true,
    tags: ["overnight", "dispersed", "high-clearance", "short-rigs"],
    whyGood:
      "BLM buttes, a 17-mile dirt loop, and a sky that does not care about Instagram. Overnight at existing pullouts, self-contained.",
    accessNotes:
      "Loop is usually 2WD when dry and a mess when wet. High-clearance is the honest recommendation. No services, no water. Mexican Hat is the nearest town. Confirm BLM dispersed rules.",
    lengthNotes:
      "Better for shorter vans than long trailers. Tight turns, washboard, and soft shoulders. If you are length-sensitive, use Goosenecks as basecamp and day-drive the loop.",
    etiquette:
      "Camp only in existing pullouts. No fires on slickrock if posted. This is not Monument Valley Navajo Park — don’t confuse the two, and don’t trespass onto tribal land.",
    officialUrl: "https://www.blm.gov/visit/valley-of-the-gods",
  },
  {
    id: "desert-view",
    name: "Grand Canyon — Desert View",
    shortName: "Desert View",
    region: "Southwest",
    state: "AZ",
    latitude: 36.0342,
    longitude: -111.8214,
    bortle: 3,
    bortleNote: "Bortle 2–3. Darker than Grand Canyon Village.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: false,
    tags: ["drive-up", "overnight", "designated-camp", "short-rigs"],
    whyGood:
      "The east rim is the dark-sky side of the South Rim. You still get the canyon, without the village lighting that washes Desert View’s bigger sibling.",
    accessNotes:
      "Desert View Drive (AZ-64). Desert View Campground is seasonal and typically first-come. No hookups. Fuel at Desert View Market when open. The village is brighter and busier — skip it for astronomy.",
    lengthNotes:
      "Small campground, modest pads. Vans yes; long RVs often no. Check current length posts.",
    etiquette:
      "Rim wind and drop-offs are real at night. Stay behind walls. Lights belong pointed at the ground, not into the canyon or other sites.",
    officialUrl: "https://www.nps.gov/grca/planyourvisit/cg-southrim.htm",
  },
  {
    id: "organ-pipe",
    name: "Organ Pipe Cactus National Monument",
    shortName: "Organ Pipe",
    region: "Southwest",
    state: "AZ",
    latitude: 31.9553,
    longitude: -112.8014,
    bortle: 2,
    bortleNote: "Bortle 2–3. Dark Sonoran sky on the borderlands.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "A desert monument with a developed campground, a dark southern Arizona sky, and a winter season that actually makes overnighting pleasant.",
    accessNotes:
      "AZ-85 south of Ajo to Twin Peaks Campground. Paved, reservable, some longer sites. Summer heat is dangerous. This is a border area — follow NPS and CBP guidance, and don’t invent your own roads.",
    lengthNotes:
      "Twin Peaks has sites that take vans and a number of longer RVs. A more length-friendly dark-sky option than most national parks on this list.",
    etiquette:
      "Stay in the campground or posted areas. Night walks on the nature trail are fine; the backcountry has extra rules. Pack extra water.",
    officialUrl: "https://www.nps.gov/orpi/index.htm",
  },
  {
    id: "chiricahua",
    name: "Chiricahua National Monument",
    shortName: "Chiricahua",
    region: "Southwest",
    state: "AZ",
    latitude: 32.0078,
    longitude: -109.3561,
    bortle: 2,
    bortleNote: "Bortle 2–3. Dark sky islands in southeast Arizona.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: false,
    tags: ["drive-up", "overnight", "designated-camp", "short-rigs"],
    whyGood:
      "Hoodoos, a small campground, and a sky-island horizon. Portal / Cave Creek nearby if you want a second night in the same dark neighborhood.",
    accessNotes:
      "Bonita Canyon Campground is small and often first-come. The monument road is paved but winding. Willcox is the supply town. Summer monsoon afternoons are a plan-around, not a surprise.",
    lengthNotes:
      "Short-rig campground. If you need length, look at Forest Service sites around Portal rather than forcing a long trailer up Bonita Canyon.",
    etiquette:
      "Bears and coatis are not theoretical. Food storage matters. Keep lighting low in a tiny campground.",
    officialUrl: "https://www.nps.gov/chir/index.htm",
  },
  {
    id: "great-basin",
    name: "Great Basin National Park",
    shortName: "Great Basin",
    region: "West",
    state: "NV",
    latitude: 39.0117,
    longitude: -114.2483,
    bortle: 1,
    bortleNote: "Bortle 1–2. International Dark Sky Park.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "One of the darkest, least-visited national parks. Lower Lehman is the van-practical campground; the astronomy programs are excellent when they run.",
    accessNotes:
      "US-6/50 to Baker, then the park road. Lower Lehman Creek is easier for vehicles than Wheeler Peak Campground. No in-park fuel. Baker and Ely are the towns. Winter access is limited at elevation.",
    lengthNotes:
      "Lower Lehman is the length-friendly choice. Wheeler Peak’s road and pads are for shorter rigs. Don’t drag a 35-footer to 10,000 feet.",
    etiquette:
      "This park takes its dark-sky status seriously. Red lights, quiet hours, food storage for wildlife. The bristlecones are not a night-hike destination without a plan.",
    officialUrl: "https://www.nps.gov/grba/index.htm",
  },
  {
    id: "death-valley-mesquite",
    name: "Death Valley — Mesquite Spring",
    shortName: "Death Valley",
    region: "West",
    state: "CA",
    latitude: 36.9663,
    longitude: -117.3684,
    bortle: 1,
    bortleNote: "Bortle 1–2. Darker than Furnace Creek.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "Death Valley is an International Dark Sky Park. Mesquite Spring, in the north, is quieter and darker than the Furnace Creek complex and still an easy paved drive.",
    accessNotes:
      "Paved to Mesquite Spring via Scotty’s Castle Road / the north end (confirm road status). First-come, no hookups. Furnace Creek has more services and more light. Summer is life-threatening heat — this is a fall–spring stay.",
    lengthNotes:
      "Mesquite Spring takes vans and many trailers. The park’s other small camps (Emigrant, wildflower lots) do not.",
    etiquette:
      "Water and shade are the scarce resources. Quiet, dark, and don’t wander washes in a storm. Generator hours are posted for a reason.",
    officialUrl: "https://www.nps.gov/deva/index.htm",
  },
  {
    id: "anza-borrego-blair",
    name: "Anza-Borrego — Blair Valley",
    shortName: "Blair Valley",
    region: "West",
    state: "CA",
    latitude: 33.1598,
    longitude: -116.3875,
    bortle: 2,
    bortleNote: "Bortle 2–3. Darker than Borrego Springs town.",
    overnight: "dispersed",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "dispersed", "length-friendly"],
    whyGood:
      "A classic self-contained overnight in Anza-Borrego: a broad valley, existing camp spots, and a sky that opens up once you leave the town of Borrego Springs.",
    accessNotes:
      "County Road S2 to Blair Valley. Usually 2WD when dry; soft sand after rain. Open camping in the valley on existing sites, no water, no toilets you should count on. Borrego Springs has fuel and food — and more light.",
    lengthNotes:
      "The valley floor is one of the more length-friendly dispersed options in Southern California. Still: no hookups, and recovery is expensive if you sink in.",
    etiquette:
      "State park rules still apply to dispersed sites. No new fire rings, no driving on the crust, quiet at night. This is a well-known spot — don’t be the group that gets it restricted.",
    officialUrl: "https://www.parks.ca.gov/?page_id=638",
  },
  {
    id: "joshua-tree-cottonwood",
    name: "Joshua Tree — Cottonwood",
    shortName: "Joshua Tree Cottonwood",
    region: "West",
    state: "CA",
    latitude: 33.7319,
    longitude: -115.811,
    bortle: 3,
    bortleNote: "Bortle 2–3. The dark end of Joshua Tree; north park is brighter.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "Cottonwood is the south entrance: quieter, usually darker, and more practical for an overnight than Jumbo Rocks on a weekend.",
    accessNotes:
      "I-10 to the south entrance. Reservable campground, pit toilets, water at the visitor center when operating. The north park (Joshua Tree / Twentynine Palms) carries more town glow and more people.",
    lengthNotes:
      "Cottonwood is more van-reasonable than several north-park loops. Still check site length; this is not an RV resort.",
    etiquette:
      "No off-road, no new cairns, no drones over the campground. Lights down after quiet hours. The desert is slow to heal.",
    officialUrl: "https://www.nps.gov/jotr/index.htm",
  },
  {
    id: "mojave-hitw",
    name: "Mojave National Preserve — Hole-in-the-Wall",
    shortName: "Hole-in-the-Wall",
    region: "West",
    state: "CA",
    latitude: 35.0436,
    longitude: -115.3967,
    bortle: 2,
    bortleNote: "Bortle 2. Big empty Mojave, far from the Strip.",
    overnight: "mixed",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "dispersed", "length-friendly"],
    whyGood:
      "A preserve campground with room to breathe and a genuinely dark Mojave sky. Dispersed camping is also legal in much of the preserve — with the usual caveats.",
    accessNotes:
      "Essex Road / Black Canyon Road from I-40. Graded dirt, usually 2WD dry. Hole-in-the-Wall and Mid Hills campgrounds are the developed options. Kelso has a depot, not a grocery store. Summer heat is serious.",
    lengthNotes:
      "Hole-in-the-Wall takes vans and many trailers. Mid Hills is higher, cooler, and a bit tighter. Dispersed sites vary — walk them first.",
    etiquette:
      "Stay on existing roads. Tortoises and cattle both live here. Pack out water jugs. Dispersed camping still needs you 200 yards from water sources where posted.",
    officialUrl: "https://www.nps.gov/moja/index.htm",
  },
  {
    id: "great-sand-dunes",
    name: "Great Sand Dunes National Park",
    shortName: "Great Sand Dunes",
    region: "West",
    state: "CO",
    latitude: 37.7394,
    longitude: -105.5286,
    bortle: 2,
    bortleNote: "Bortle 2–3. Dark San Luis Valley with a dune foreground.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "International Dark Sky Park energy with a surreal foreground. Piñon Flats is the easy overnight; the valley around the park is darker still.",
    accessNotes:
      "CO-150 to Piñon Flats Campground (reservable). Paved. Alamosa is the supply town. Medano Pass primitive road is 4WD and a different trip. Nights are cold even in summer.",
    lengthNotes:
      "Piñon Flats has a mix; many van-length sites. The 4WD camps beyond the creek are not for long or low vehicles.",
    etiquette:
      "Sand and creek crossings chew up bad decisions. Stay in numbered sites at Piñon Flats. The dunes are open at night — don’t light-paint other people’s photos.",
    officialUrl: "https://www.nps.gov/grsa/index.htm",
  },
  {
    id: "alvord-desert",
    name: "Alvord Desert",
    shortName: "Alvord Desert",
    region: "West",
    state: "OR",
    latitude: 42.38,
    longitude: -118.45,
    bortle: 1,
    bortleNote: "Bortle 1–2. One of the darkest drive-up playas in the Lower 48.",
    overnight: "dispersed",
    driveUp: true,
    lengthFriendly: true,
    highClearance: true,
    tags: ["drive-up", "overnight", "dispersed", "length-friendly", "high-clearance"],
    whyGood:
      "A huge alkali playa with almost nothing on the horizon. If you want a multi-night stay under a true dark sky in a self-contained van, this is the archetype.",
    accessNotes:
      "Remote southeast Oregon east of Fields / Princeton. Washboard BLM roads; usually 2WD when dry. The playa is impassable when wet — “a little damp” is how people get extracted. No services. Fields Station is the lifeline. Check BLM and playa condition reports.",
    lengthNotes:
      "The playa can take almost any van when bone-dry. Soft spots are not obvious. If in doubt, camp on the gravel apron, not the white lakebed.",
    etiquette:
      "Leave no tracks that don’t already exist, pack out every bottle, and skip fireworks and drone swarms. Land-speed people and solitude people share this space poorly if you show up loud.",
    officialUrl: "https://www.blm.gov/visit/alvord-desert",
  },
  {
    id: "bruneau-dunes",
    name: "Bruneau Dunes State Park",
    shortName: "Bruneau Dunes",
    region: "West",
    state: "ID",
    latitude: 42.911,
    longitude: -115.7075,
    bortle: 3,
    bortleNote: "Bortle 2–3. On-site observatory; easy developed camping.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "A state park that actually leans into astronomy: dunes, a public observatory, and a campground you can reserve without a dirt-road thesis.",
    accessNotes:
      "South of Mountain Home off ID-51 / ID-78. Paved, developed camping with hookup loops available. Observatory programs are seasonal. Boise is close enough that this is not a Bortle-1 sanctuary — it is a practical dark-ish overnight.",
    lengthNotes:
      "One of the easiest length-friendly parks on the list. Standard state-park pads, including for larger vans and many trailers.",
    etiquette:
      "Observatory nights mean extra care with white lights. Stay off the dune faces with vehicles. Quiet hours include the observatory parking.",
    officialUrl: "https://parksandrecreation.idaho.gov/parks/bruneau-dunes/",
  },
  {
    id: "denali-np",
    name: "Denali National Park",
    shortName: "Denali",
    region: "Alaska",
    country: "US",
    state: "AK",
    latitude: 63.728,
    longitude: -148.894,
    bortle: 2,
    bortleNote: "Bortle 1–2. Entrance loops see some Healy glow; Wonder Lake is darker.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    permit: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly", "permit"],
    whyGood:
      "Alaska Range nights with a real campground, not a fly-in. Aurora on the shoulder seasons, Milky Way in the short summer dark, and a park road that still means something.",
    accessNotes:
      "Parks Highway to Riley Creek near the entrance — the practical van overnight. Private vehicles stop at Savage River unless you have a road-lottery / campground permit farther in. Wonder Lake is the darker stay if you win that. Mosquitoes, buses, and reservations are the other weather.",
    lengthNotes:
      "Riley Creek takes typical vans and many trailers. Interior campgrounds are tighter and often tent/small-rig. Do not assume the park road is your Class A driveway.",
    etiquette:
      "Wildlife is the land manager. Food in lockers, not the picnic table. Night lighting stays red and low. The park road is not a dispersed BLM track.",
    officialUrl: "https://www.nps.gov/dena/",
  },
  {
    id: "tangle-lakes",
    name: "Tangle Lakes / Denali Highway",
    shortName: "Tangle Lakes",
    region: "Alaska",
    country: "US",
    state: "AK",
    latitude: 63.043,
    longitude: -146.004,
    bortle: 1,
    bortleNote: "Bortle 1. Open tundra, almost no towns.",
    overnight: "mixed",
    driveUp: true,
    lengthFriendly: false,
    highClearance: true,
    tags: ["drive-up", "overnight", "dispersed", "designated-camp", "short-rigs", "high-clearance"],
    whyGood:
      "The gravel Denali Highway is the Alaska dark-sky drive: BLM campgrounds, tundra horizon, and a sky that does not care about your schedule. Better darkness than the Denali entrance, with none of the bus system.",
    accessNotes:
      "Paxson or Cantwell onto the Denali Highway (AK-8). Gravel, washboard, usually 2WD when dry. Tangle Lakes Campground is the named stay; informal pull-offs exist — confirm current BLM rules. No services for a long way. Closed in winter. Bugs in July are a personality.",
    lengthNotes:
      "Fine for vans and short trailers. Long coaches and low-clearance rigs should stay on the Parks or Richardson instead.",
    etiquette:
      "This is hunting, fishing, and subsistence country. Pack out, stay off wet tundra with tires, and do not treat a pull-off as a private lot.",
    officialUrl: "https://www.blm.gov/visit/tangle-lakes-campground",
  },
  {
    id: "white-mountains-nra",
    name: "White Mountains National Recreation Area",
    shortName: "White Mountains",
    region: "Alaska",
    country: "US",
    state: "AK",
    latitude: 65.348,
    longitude: -146.915,
    bortle: 2,
    bortleNote: "Bortle 2. Fairbanks glow to the south; the north sky is the aurora oval.",
    overnight: "mixed",
    driveUp: true,
    lengthFriendly: false,
    highClearance: true,
    tags: ["drive-up", "overnight", "dispersed", "short-rigs", "high-clearance"],
    whyGood:
      "The Fairbanks-side pin for people who came for aurora and still want a legal overnight: BLM White Mountains, Nome Creek, and a dark northern horizon an hour from town.",
    accessNotes:
      "Steese Highway / Nome Creek Road north of Fairbanks. Dirt, seasonal, mud when it wants. BLM campgrounds and some cabin permits. Winter is a different trip (snowmachine / ski). Fairbanks is groceries and the airport.",
    lengthNotes:
      "Nome Creek is a small-rig and van road. Leave the 35-foot fifth-wheel in Fairbanks.",
    etiquette:
      "BLM land with cabins that other people reserved. Bear and moose country. Lights down if you actually want the oval.",
    officialUrl: "https://www.blm.gov/programs/recreation/recreation-activities/alaska/white-mountains-national-recreation-area",
  },
  {
    id: "jasper-np",
    name: "Jasper National Park",
    shortName: "Jasper",
    region: "Canada",
    country: "CA",
    state: "AB",
    latitude: 52.877,
    longitude: -118.077,
    bortle: 2,
    bortleNote: "Bortle 1–2. Townsite has lights; the Icefields Parkway does not.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    permit: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly", "permit"],
    whyGood:
      "Second-largest dark-sky preserve on the planet, with frontcountry campgrounds that take real RVs. October Dark Sky Festival if you want a crowd on purpose.",
    accessNotes:
      "Icefields Parkway or Yellowhead to Jasper. Whistlers and Wapiti are the length-friendly stays; Icefields Centre RV is a simpler pad near the glacier. Reservations in summer are not optional. No dispersed camping in the park.",
    lengthNotes:
      "Whistlers handles large rigs. Some Icefields Parkway loops are vans and short trailers. The townsite is not the sky.",
    etiquette:
      "Parks Canada campgrounds only. Bear lockers. Red light after quiet hours. Passport at the border; this is not a Texas state park.",
    officialUrl: "https://parks.canada.ca/pn-np/ab/jasper",
  },
  {
    id: "grasslands-np",
    name: "Grasslands National Park",
    shortName: "Grasslands",
    region: "Canada",
    country: "CA",
    state: "SK",
    latitude: 49.123,
    longitude: -107.526,
    bortle: 1,
    bortleNote: "Bortle 1. One of the darkest drive-up skies on the continent.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    permit: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly", "permit"],
    whyGood:
      "Prairie horizon, bison, and a sky with nothing in the way. Frenchman Valley (West Block) is the van-practical stay; East Block is farther and quieter.",
    accessNotes:
      "Val Marie, then gravel into the West Block. Frenchman Valley Campground has electrical sites; fill RV tanks before you arrive. East Block / Rock Creek is a two-hour separate drive. Parks Canada reservation. Wind is the other forecast.",
    lengthNotes:
      "Electrical loops take typical trailers and vans. Gravel access is the limiter, not pad length.",
    etiquette:
      "Bison have right of way. Stay on roads. Night observing is allowed in the park — do not set up on a bison trail and call it a pad.",
    officialUrl: "https://parks.canada.ca/pn-np/sk/grasslands",
  },
  {
    id: "cypress-hills",
    name: "Cypress Hills Interprovincial Park",
    shortName: "Cypress Hills",
    region: "Canada",
    country: "CA",
    state: "AB",
    latitude: 49.662,
    longitude: -110.291,
    bortle: 2,
    bortleNote: "Bortle 2. Canada’s first dark-sky preserve; townsite glow is modest.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly"],
    whyGood:
      "A forested plateau on the prairie with hookup campgrounds, an observatory, and a RASC dark-sky preserve that is actually easy to sleep in. Elkwater (Alberta) is the length-friendly side.",
    accessNotes:
      "Medicine Hat, then Elkwater on the Alberta side, or Maple Creek / Centre Block in Saskatchewan. Both sides have developed camping. Centre Block runs the summer star party. Not a wilderness pin — services exist, which is the point.",
    lengthNotes:
      "Elkwater and Centre Block take real RVs, including electric loops. Wilderness West Block is rustic.",
    etiquette:
      "Observatory and Dark Sky Campground nights mean extra care with white lights. Two provinces, two reservation systems — pick a side and book it.",
    officialUrl: "https://www.albertaparks.ca/parks/south/cypress-hills-pp/",
  },
  {
    id: "wood-buffalo-np",
    name: "Wood Buffalo National Park",
    shortName: "Wood Buffalo",
    region: "Canada",
    country: "CA",
    state: "NT",
    latitude: 59.552,
    longitude: -112.248,
    bortle: 1,
    bortleNote: "Bortle 1. Largest dark-sky preserve in the world.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    permit: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly", "permit"],
    whyGood:
      "The biggest protected night sky on Earth, with aurora, bison, and a short season. Pine Lake is the frontcountry overnight if you made the drive to Fort Smith.",
    accessNotes:
      "Fly or drive to Fort Smith, NWT, then south to Pine Lake Campground. This is a commitment, not a weekend from Edmonton without planning. Parks Canada. Summer only for most camping. Biting insects are not a footnote.",
    lengthNotes:
      "Pine Lake takes vans and many trailers. The adventure is the distance, not the pad.",
    etiquette:
      "UNESCO, Indigenous territory, and a working bison range. Stay in the campground. Passport. This is not a rest-area nap.",
    officialUrl: "https://parks.canada.ca/pn-np/nt/woodbuffalo",
  },
  {
    id: "mont-megantic",
    name: "Parc national du Mont-Mégantic",
    shortName: "Mont-Mégantic",
    region: "Canada",
    country: "CA",
    state: "QC",
    latitude: 45.456,
    longitude: -71.152,
    bortle: 2,
    bortleNote: "Bortle 2–3. First IDA Dark Sky Reserve (2007); ASTROLab on the mountain.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    permit: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly", "permit"],
    whyGood:
      "The original certified dark-sky reserve, with a public observatory and a Québec national-park campground. The eastern-Canada pin so the map is not only prairies and Rockies.",
    accessNotes:
      "East of Sherbrooke / Notre-Dame-des-Bois. Sépaq campground in the park; ASTROLab programs are ticketed. French is the working language; English is common at the observatory. Reserve camping.",
    lengthNotes:
      "Park loops take typical vans and many trailers. The summit road is for the observatory visit, not overnighting the coach on the peak.",
    etiquette:
      "The reserve exists because the towns dimmed their lights. Return the favor. No white floods from the campground.",
    officialUrl: "https://www.sepaq.com/pq/mme/",
  },
  {
    id: "kejimkujik-np",
    name: "Kejimkujik National Park",
    shortName: "Kejimkujik",
    region: "Canada",
    country: "CA",
    state: "NS",
    latitude: 44.399,
    longitude: -65.218,
    bortle: 2,
    bortleNote: "Bortle 2. RASC Dark-Sky Preserve; Atlantic humidity is the other forecast.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    permit: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly", "permit"],
    whyGood:
      "Mi’kma’ki lakes and a protected night sky in southwest Nova Scotia. Jeremy’s Bay is the drive-up campground; the dark is real when the marine layer stays offshore.",
    accessNotes:
      "Highway 8 between Annapolis Royal and Liverpool. Parks Canada reservations for Jeremy’s Bay. Seaside adjunct is a different unit (more fog, fewer pads). This is not a desert pin — pack for dew.",
    lengthNotes:
      "Jeremy’s Bay takes vans and many trailers. Interior canoe sites are not the van trip.",
    etiquette:
      "This is Mi’kmaq cultural landscape as well as a park. Quiet hours, no shoreline driving, red light around the lake.",
    officialUrl: "https://parks.canada.ca/pn-np/ns/kejimkujik",
  },
  {
    id: "tombstone-territorial",
    name: "Tombstone Territorial Park",
    shortName: "Tombstone",
    region: "Canada",
    country: "CA",
    state: "YT",
    latitude: 64.512,
    longitude: -138.22,
    bortle: 1,
    bortleNote: "Bortle 1. Open tundra under the Ogilvie Mountains.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: false,
    highClearance: true,
    permit: true,
    tags: ["drive-up", "overnight", "designated-camp", "short-rigs", "high-clearance", "permit"],
    whyGood:
      "The Yukon pin: granite tors, Dempster Highway, and a sky that is the reason people drive the gravel. Designated campground at the interpretive centre, not a random pull-off.",
    accessNotes:
      "Dempster Highway (Hwy 5) to km ~72. Gravel, frost heaves, no services. Interpretive centre campground is the stay. Dawson City is the last real town. Summer only for most vehicles. Register as posted.",
    lengthNotes:
      "Vans and short rigs. The Dempster is not a fifth-wheel highway. Fuel range is the other length limit.",
    etiquette:
      "Tr’ondëk Hwëch’in land, territorial park rules. Stay in the campground. Pack out. The highway is a working road, not a playground.",
    officialUrl: "https://yukon.ca/en/tombstone-territorial-park",
  },
  {
    id: "kluane-np",
    name: "Kluane National Park",
    shortName: "Kluane",
    region: "Canada",
    country: "CA",
    state: "YT",
    latitude: 60.573,
    longitude: -137.355,
    bortle: 2,
    bortleNote: "Bortle 1–2. Icefield country; Haines Junction is a small glow.",
    overnight: "designated",
    driveUp: true,
    lengthFriendly: true,
    permit: true,
    tags: ["drive-up", "overnight", "designated-camp", "length-friendly", "permit"],
    whyGood:
      "Kathleen Lake under the St. Elias icefields — a Parks Canada campground on the Alaska Highway, dark enough to matter, drive-up enough to use.",
    accessNotes:
      "Haines Junction, then a short park road to Kathleen Lake Campground. Alaska Highway is the approach from Whitehorse or Tok. Parks Canada. Shoulder seasons get aurora; summer nights are short.",
    lengthNotes:
      "Kathleen Lake takes vans and many trailers. Not a 40-foot resort loop, but friendlier than Tombstone.",
    etiquette:
      "Champagne and Aishihik First Nations territory. Bear country. Campground only. Passport if you came up from Alaska.",
    officialUrl: "https://parks.canada.ca/pn-np/yt/kluane",
  },
  {
    id: "san-pedro-martir",
    name: "Sierra de San Pedro Mártir National Park",
    shortName: "San Pedro Mártir",
    region: "Mexico",
    country: "MX",
    state: "BCN",
    latitude: 31.0,
    longitude: -115.557,
    bortle: 1,
    bortleNote: "Bortle 1. Mexico’s professional observatory sky; 300+ clear nights.",
    overnight: "mixed",
    driveUp: true,
    lengthFriendly: false,
    highClearance: true,
    permit: true,
    tags: ["drive-up", "overnight", "dispersed", "designated-camp", "short-rigs", "high-clearance", "permit"],
    whyGood:
      "The darkest easy-ish sky in Mexico: 2,800 m pines, UNAM’s national observatory, and rustic camping included with the park ticket. Baja’s sky island.",
    accessNotes:
      "Transpeninsular (Hwy 1) south of Ensenada / San Quintín, then ~100 km of mountain road to the park. Last stretch is dirt; high-clearance when wet or snowed. Camping near the entrance is rustic (tables, pit toilets, fire rings). Observatory visits are scheduled — not a 24-hour public dome. Mexican park pass / CONANP fee. Vehicle TIP and FMM if you crossed from the US.",
    lengthNotes:
      "Tents and small RVs. A long coach does not belong on the observatory road.",
    etiquette:
      "The astronomers picked this ridge for a reason. Lights out. No off-road. Dogs under control. Do not treat the observatory gate as a campsite.",
    officialUrl: "https://www.gob.mx/conanp",
  },
  {
    id: "pinacate",
    name: "El Pinacate y Gran Desierto de Altar",
    shortName: "Pinacate",
    region: "Mexico",
    country: "MX",
    state: "SON",
    latitude: 31.714,
    longitude: -113.528,
    bortle: 2,
    bortleNote: "Bortle 2. Volcanic craters on the Altar Desert; Organ Pipe is the US neighbor.",
    overnight: "mixed",
    driveUp: true,
    lengthFriendly: false,
    highClearance: true,
    permit: true,
    tags: ["drive-up", "overnight", "dispersed", "short-rigs", "high-clearance", "permit"],
    whyGood:
      "UNESCO biosphere of craters and sand sea, with a sky that matches Organ Pipe without the Arizona campground lottery. The Sonoran pin across the line.",
    accessNotes:
      "Mexico Hwy 8 between Sonoita/Lukeville and Puerto Peñasco. Schuk Toak visitor center is the orientation; camping is rustic / designated where posted — confirm CONANP current rules before you assume a crater rim is a pad. Extreme heat. Puerto Peñasco is the service town (and the light dome to avoid).",
    lengthNotes:
      "Visitor-center roads take vans. Interior sand and crater tracks are high-clearance, short-rig, or hike.",
    etiquette:
      "Biosphere reserve, Tohono O’odham and Mexican federal land. Stay on posted routes. No crater camping unless it is a marked site. Passport, TIP, and don’t skip the military checkpoint attitude.",
    officialUrl: "https://www.gob.mx/conanp",
  },
  {
    id: "canon-santa-elena",
    name: "Cañón de Santa Elena / Maderas del Carmen",
    shortName: "Santa Elena (MX)",
    region: "Mexico",
    country: "MX",
    state: "CHIH",
    latitude: 29.15,
    longitude: -103.55,
    bortle: 1,
    bortleNote: "Bortle 1. Mexico half of the Greater Big Bend International Dark Sky Reserve.",
    overnight: "mixed",
    driveUp: true,
    lengthFriendly: false,
    highClearance: true,
    permit: true,
    tags: ["drive-up", "overnight", "dispersed", "short-rigs", "high-clearance", "permit"],
    whyGood:
      "The same sky as Big Bend, south of the river: Santa Elena, Ocampo, and Maderas del Carmen are in the binational reserve. A Mexican pin so the map does not stop at the Rio Grande.",
    accessNotes:
      "This is not a Terlingua Saturday. Approach via Ojinaga / Manuel Benavides or the Boquillas crossing when it is open. Ejido roads, CONANP protected areas, limited services. Camp only where the land manager or ejido hosts you. Confirm the crossing and permits before you commit a rig.",
    lengthNotes:
      "High-clearance vans and short rigs. A big trailer belongs on the Texas side of the reserve.",
    etiquette:
      "Border, ranch, and protected land. Closed gates stay closed. No freelance night hikes into a neighbor’s pasture for a time-lapse. Passport. Pack out.",
    officialUrl: "https://www.gob.mx/conanp",
  },
  {
    id: "catavina",
    name: "Cataviña / Valle de los Cirios",
    shortName: "Cataviña",
    region: "Mexico",
    country: "MX",
    state: "BCN",
    latitude: 29.732,
    longitude: -114.719,
    bortle: 1,
    bortleNote: "Bortle 1. Central Baja boulder desert on the Transpeninsular.",
    overnight: "mixed",
    driveUp: true,
    lengthFriendly: true,
    tags: ["drive-up", "overnight", "dispersed", "length-friendly"],
    whyGood:
      "Highway 1 through the Valle de los Cirios: cardón, granite, and a sky with no city attached. The Baja road-trip overnight that is actually dark, not a beach town.",
    accessNotes:
      "Transpeninsular around Km 174, Cataviña. Hotel / RV parking in town when open; boulder-field camping is traditional but confirm current CONANP / local rules — this is a protected flora area, not BLM. Fuel range is real. Do not drive the highway at night (livestock, no shoulders).",
    lengthNotes:
      "The highway itself takes any rig. Side tracks into the boulders do not. Sleep where you are clearly allowed, then walk for the sky.",
    etiquette:
      "Protected desert. No cutting cardón, no new roads, pack out every bottle. TIP and FMM. The sky is free; the land is not abandoned.",
    officialUrl: "https://www.gob.mx/conanp",
  },
];

export function siteById(id: string): Site | undefined {
  return SITES.find((site) => site.id === id);
}

export const REGIONS: Array<Site["region"]> = [
  "Texas",
  "Southwest",
  "West",
  "Alaska",
  "Canada",
  "Mexico",
];
